<script setup lang="ts">
type Props = {
  showLogo?: boolean;
  showBanner?: boolean;
  largeContent?: boolean;
  smallCloseButton?: boolean;
};

const {
  showLogo = true,
  showBanner = true,
  largeContent = false,
  smallCloseButton = false,
} = defineProps<Props>();

type Emits = {
  actionToClose: [];
};

const $emit = defineEmits<Emits>();

const { isMobile } = useLayout();
const { isAuthV2FlowEnabled } = useAuth();
const { openHelp } = useLayoutStore();
const { currentRouteName } = useRoutes();
const { showModalImage } = useModalImage();

const hideCloseButton = computed(() => {
  return (
    currentRouteName.value === 'pending' ||
    currentRouteName.value?.includes('migrate')
  );
});

const isPendingPage = computed(() => {
  return currentRouteName.value === 'pending';
});

const isMigratePage = computed(() => {
  return currentRouteName.value?.includes('migrate');
});

function onClickHelpButton() {
  openHelp();
}
</script>

<template>
  <div :class="$style.authPage">
    <template v-if="isAuthV2FlowEnabled || isPendingPage || isMigratePage">
      <div
        :class="[
          $style.newAuthContainer,
          { [$style.withoutImage]: !showModalImage },
          { [$style.largeContent]: largeContent },
        ]"
      >
        <!-- Topbar Mobile: Logo + Ajuda + X sobre o banner -->
        <div
          v-if="isMobile"
          class="absolute top-0 left-0 right-0 z-20 px-4 py-3 flex justify-between pointer-events-none"
        >
          <div class="flex flex-col pointer-events-auto">
            <BaseCommonLogo
              v-if="!!showLogo"
              :allow-redirect="false"
              always-logo
              class="h-5 mt-2"
              :custom-size="true"
            />
          </div>

          <div class="flex items-center gap-3 pointer-events-auto">
            <div
              aria-label="Abrir ajuda"
              class="px-2 py-[5px] bg-white/5 rounded-full flex justify-center items-center gap-1 cursor-pointer mt-0"
              role="button"
              tabindex="0"
              @click="onClickHelpButton"
            >
              <NuxtIcon
                class="mr-1 text-[12px] text-white/60"
                name="auth/ajuda"
              />
              <div class="text-white/70 text-[10px] font-medium leading-3">
                Precisa de ajuda?
              </div>
            </div>
            <div
              v-if="!hideCloseButton"
              aria-label="Fechar"
              class="flex items-center justify-center cursor-pointer w-5 h-5"
              role="button"
              @click="$emit('actionToClose')"
            >
              <NuxtIcon
                class="flex text-center justify-center text-white hover:text-white transition font-extrabold text-[20px]"
                name="close"
              />
            </div>
          </div>
        </div>

        <div :class="[$style.dynamicContent, isMobile ? 'mt-[56px]' : '']">
          <div
            v-if="showBanner"
            :class="[
              $style.modalImage,
              { [$style.withoutImage]: !showModalImage },
            ]"
          >
            <BaseModalImage />
          </div>

          <div :class="$style.modalContent">
            <div
              class="w-full max-w-full sm:max-w-[482px] self-stretch px-4 sm:px-7 py-5 inline-flex flex-col justify-start items-center gap-5 flex-1 overflow-y-auto md:overflow-visible"
            >
              <div
                v-if="!isMobile"
                class="self-stretch sticky top-0 z-20 bg-bg-primary/90 backdrop-blur inline-flex justify-between items-center gap-5 px-4 py-3"
              >
                <div class="flex items-center gap-2">
                  <div
                    aria-label="Abrir ajuda"
                    class="px-2.5 py-[5px] bg-white/5 rounded-full flex justify-center items-center gap-1 cursor-pointer"
                    role="button"
                    tabindex="0"
                    @click="onClickHelpButton"
                  >
                    <div class="w-3 h-3 relative overflow-hidden">
                      <NuxtIcon
                        class="mr-2 text-[12px] text-white/50"
                        name="auth/ajuda"
                      />
                    </div>
                    <div
                      class="justify-center text-white/50 text-xs font-medium leading-3"
                    >
                      Precisa de ajuda?
                    </div>
                  </div>
                </div>
                <div
                  v-if="!hideCloseButton"
                  aria-label="Fechar"
                  :class="[
                    'flex items-center justify-center cursor-pointer',
                    smallCloseButton ? 'w-4 h-4' : 'w-5 h-5',
                  ]"
                  role="button"
                  @click="$emit('actionToClose')"
                >
                  <NuxtIcon
                    :class="[
                      'flex text-center justify-center text-white hover:text-white transition font-extrabold',
                      smallCloseButton ? 'text-[18px]' : 'text-[25px]',
                    ]"
                    name="close"
                  />
                </div>
              </div>
              <BaseCommonLogo
                v-if="!!showLogo && !isMobile"
                :allow-redirect="false"
                always-logo
                :class="$style.logoContainer"
              />

              <slot />
            </div>

            <div
              id="dynamicContentFooterContainer"
              class="relative"
            />
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div :class="[$style.oldAuthContainer, isMobile ? 'pt-[56px]' : '']">
        <!-- Topbar Mobile (old flow): Logo + Ajuda + X sobre o banner -->
        <div
          v-if="isMobile"
          class="absolute top-0 left-0 right-0 z-20 px-4 py-3 flex items-center justify-between pointer-events-none"
        >
          <div class="pointer-events-auto">
            <BaseCommonLogo
              v-if="showLogo"
              :allow-redirect="false"
              always-logo
              class="h-6"
              :custom-size="true"
            />
          </div>

          <div class="flex items-center gap-3 pointer-events-auto">
            <div
              aria-label="Abrir ajuda"
              class="px-2 py-[5px] bg-white/5 rounded-full flex justify-center items-center gap-1 cursor-pointer"
              role="button"
              tabindex="0"
              @click="onClickHelpButton"
            >
              <NuxtIcon
                class="mr-1 text-[12px] text-white/60"
                name="auth/ajuda"
              />
              <div class="text-white/70 text-[10px] font-medium leading-3">
                Precisa de ajuda?
              </div>
            </div>

            <div
              v-if="!hideCloseButton"
              aria-label="Fechar"
              class="flex items-center justify-center cursor-pointer w-5 h-5"
              role="button"
              @click="$emit('actionToClose')"
            >
              <NuxtIcon
                class="flex text-center justify-center text-white hover:text-white transition font-extrabold text-[20px]"
                name="close"
              />
            </div>
          </div>
        </div>
        <BaseModalCloseButton
          v-if="!isMobile"
          :class="$style.closeButton"
          @close="$emit('actionToClose')"
        />

        <section
          :class="[
            $style.bannerImage,
            { [$style.withoutImage]: !showModalImage },
          ]"
        >
          <BaseModalImage />
        </section>

        <section :class="$style.slotContainer">
          <BaseModalTabs />

          <BaseCommonLogo
            v-if="showLogo && !isMobile"
            :allow-redirect="false"
            always-logo
            :class="$style.logoContainer"
          />

          <div :class="$style.slotContent">
            <slot />
          </div>
        </section>

        <div
          id="dynamicContentFooterContainer"
          class="relative w-full mt-auto"
        />
      </div>
    </template>
  </div>
</template>

<style module lang="scss">
.authPage {
  @apply rounded-[10px];
  .logoContainer {
    @apply w-full flex justify-center;
  }

  .newAuthContainer {
    @apply w-full h-full md:h-auto max-w-[950px] mx-auto flex flex-col relative rounded-[10px] bg-bg-primary shadow-none md:shadow-xl md:shadow-black/30 overflow-hidden min-h-[100dvh] md:min-h-0;

    .dynamicContent {
      @apply flex flex-col md:flex-row flex-1 bottom-0;

      .modalImage {
        @apply w-full md:w-[450px] md:max-w-[50vw] p-0 h-auto md:h-[750px];

        img {
          @apply rounded-none md:rounded-none object-cover w-full h-full;
        }

        &.withoutImage {
          @apply hidden;
        }
      }

      .modalContent {
        @apply flex-1 flex flex-col justify-between md:w-1/2;

        .logoContainer {
          @apply mb-1;
        }
      }
    }

    &.withoutImage:not(.largeContent) {
      @apply md:max-w-[500px];
    }
  }

  .oldAuthContainer {
    @apply w-full h-full sm:h-auto flex flex-col justify-start items-center mx-auto sm:max-w-[440px] bg-bg-primary rounded-[10px] shadow-xl shadow-black/30 relative overflow-hidden;

    .closeButton {
      @apply top-2 right-2;
    }

    .bannerImage {
      @apply p-0 mb-2;
    }

    .slotContainer {
      @apply w-full flex flex-col gap-2;

      .slotContent {
        @apply flex-1 p-6;
      }
    }
  }
}
</style>
